/**
 * One hook for a screen that must work with no signal.
 *
 * `useLocal` reads the device's store and re-reads it whenever a sync lands,
 * so a screen never shows a spinner for data that is already on the phone. It
 * never goes to the network itself: the sync engine does that on its own
 * schedule, and a screen that also fetched would race it.
 *
 * That separation is the point. The alternative — every screen fetching, with
 * the store as a cache behind it — puts the decision about what is authoritative
 * in forty components instead of one, and they disagree the first time somebody
 * adds a `staleTime`.
 */

import { useCallback, useEffect, useState } from "react";

import { useOffline } from "./offline";

type State<T> = {
  data: T | undefined;
  /** Only true before the first read. A refresh does not blank the screen. */
  isPending: boolean;
  error: unknown;
  reload: () => void;
};

/**
 * Read something from the store.
 *
 * `deps` are what the read is keyed by — an id, a user. `read` is re-run when
 * they change and after every sync, so an edit that arrives from the office
 * appears without the tech doing anything.
 */
export function useLocal<T>(read: () => Promise<T>, deps: unknown[]): State<T> {
  const { state } = useOffline();
  const [data, setData] = useState<T | undefined>(undefined);
  const [isPending, setPending] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [nonce, setNonce] = useState(0);

  // `state` changes after every sync; `cursor` moving means something landed.
  const cursor = state?.cursor ?? 0;
  const pending = state?.pending ?? 0;
  // A refusal arriving or being dismissed changes what some screens show —
  // the install register puts a refused scan back on the worklist, and takes
  // the warning away once it is dismissed.
  const refused = state?.refused ?? 0;

  useEffect(() => {
    let live = true;
    read()
      .then((value) => {
        if (!live) return;
        setData(value);
        setError(null);
      })
      .catch((cause) => {
        if (live) setError(cause);
      })
      .finally(() => {
        if (live) setPending(false);
      });
    return () => {
      live = false;
    };
    // `read` is intentionally not a dependency: it is a fresh closure every
    // render, and including it would re-read on every render forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, cursor, pending, refused, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, isPending, error, reload };
}
